import {DecimalPipe} from "@angular/common"
import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from "@angular/core"
import {MatDialog} from "@angular/material/dialog"
import {Box2Like} from "@cm/math"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {SliderComponent} from "@common/components/inputs/slider/slider.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {DropFilesComponent} from "@common/components/files/drop-files/drop-files.component"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {HintComponent} from "@common/components/hint/hint.component"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {ColorInputComponent} from "@common/components/inputs/color-input/color-input.component"
import {MimeType} from "@legacy/helpers/utils"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {DialogService} from "@common/services/dialog/dialog.service"
import {OperatorRecolor} from "@app/textures/texture-editor/operator-stack/operators/recolor/operator-recolor"
import {OperatorPanelBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-panel-base"
import {computeLabMeanFromColor} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/compute-lab-mean-from-color"
import {RecolorReferenceTextureMap} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/recolor-reference-image"
import {ReferenceRegionSelectorComponent} from "@app/textures/texture-editor/operator-stack/operators/recolor/panel/reference-region-selector/reference-region-selector.component"
import {
    ImportTextureSetDialogComponent,
    ImportTextureSetDialogData,
    ImportTextureSetDialogResult,
} from "@app/textures/import-texture-set-dialog/import-texture-set-dialog.component"
import {SelectMaterialComponent} from "@platform/components/materials/select-material/select-material.component"

export enum RecolorTargetStatsMode {
    TextureSetRevision = "texture-set-revision",
    ReferenceImage = "reference-image",
    ColorPicker = "color-picker",
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-recolor-panel",
    templateUrl: "./recolor-panel.component.html",
    styleUrls: ["./recolor-panel.component.scss"],
    imports: [
        DecimalPipe,
        InspectorSectionComponent,
        SliderComponent,
        InputContainerComponent,
        NumericInputComponent,
        DropFilesComponent,
        LoadingSpinnerIconComponent,
        HintComponent,
        ButtonComponent,
        ColorInputComponent,
        ReferenceRegionSelectorComponent,
    ],
})
export class RecolorPanelComponent extends OperatorPanelBase<OperatorRecolor> implements OnInit {
    protected readonly MimeType = MimeType
    protected readonly targetStatsModes = RecolorTargetStatsMode

    private readonly notifications = inject(NotificationsService)
    private readonly dialogs = inject(DialogService)
    private readonly matDialog = inject(MatDialog)

    protected readonly $computingTargetStats = computed(() => this.$operator().$computingTargetStats())
    protected readonly $targetStatsMode = signal(RecolorTargetStatsMode.TextureSetRevision)
    protected readonly $isDebugEnabled = computed(() => this.$operator().callback.isDebugEnabled ?? false)

    protected readonly $referenceImage = computed(() => this.$operator().$referenceImage())
    protected readonly $referenceRegion = computed(() => this.$operator().$referenceRegion())
    protected readonly $referenceUnavailable = computed(() => this.$operator().$referenceUnavailable())
    protected readonly $referenceControlsDisabled = computed(() => this.$disabledEdits() || this.$computingTargetStats())

    // the reference image, if it is one the current tab is about
    protected readonly $tabReferenceImage = computed(() => {
        const referenceImage = this.$referenceImage()
        switch (this.$targetStatsMode()) {
            case RecolorTargetStatsMode.TextureSetRevision:
                return referenceImage?.textureMap ? referenceImage : undefined
            case RecolorTargetStatsMode.ReferenceImage:
                return referenceImage?.textureMap ? undefined : referenceImage
            case RecolorTargetStatsMode.ColorPicker:
                return undefined
        }
    })

    protected readonly $referenceRegionLabel = computed(() => {
        const region = this.$referenceRegion()
        return region ? `${region.width} × ${region.height} px at ${region.x}, ${region.y}` : "Entire image"
    })

    ngOnInit(): void {
        const operator = this.$operator()
        const referenceImage = operator.$referenceImage()
        if (referenceImage && !referenceImage.textureMap) {
            this.$targetStatsMode.set(RecolorTargetStatsMode.ReferenceImage)
        }
        void operator.restorePersistedReference()
    }

    protected setTargetStatsMode(mode: RecolorTargetStatsMode): void {
        this.$targetStatsMode.set(mode)
    }

    // starts at the edited texture's own material, from where the user can go back to pick another one
    protected chooseReferenceTextureMap(): void {
        if (this.$referenceControlsDisabled()) return
        const material = this.$operator().callback.revision.textureSet.textureGroup.material
        if (material) {
            this.openReferenceTextureMapDialog(material)
        } else {
            this.selectMaterialForReferenceTextureMap()
        }
    }

    private selectMaterialForReferenceTextureMap(): void {
        this.dialogs.selectInDialog(SelectMaterialComponent, {
            filters: {organizationId: {in: [this.$operator().callback.organization.id]}},
            lockOrganization: true,
            onSelect: (material) => this.openReferenceTextureMapDialog(material),
        })
    }

    private openReferenceTextureMapDialog(sourceMaterial: ImportTextureSetDialogData["sourceMaterial"]): void {
        this.matDialog
            .open<ImportTextureSetDialogComponent, ImportTextureSetDialogData, ImportTextureSetDialogResult>(ImportTextureSetDialogComponent, {
                data: {sourceMaterial, selectTextureMap: {defaultTextureType: this.$operator().defaultReferenceTextureType}},
                width: "800px",
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.outcome === "back") {
                    this.selectMaterialForReferenceTextureMap()
                } else if (result?.outcome === "selected") {
                    void this.onReferenceTextureMapSelected({textureSetRevisionId: result.textureSetRevisionId, textureType: result.textureType})
                }
            })
    }

    private async onReferenceTextureMapSelected(textureMap: RecolorReferenceTextureMap): Promise<void> {
        try {
            await this.$operator().setReferenceFromTextureMap(textureMap)
        } catch {
            this.notifications.showErrorString("Could not load the reference texture.")
        }
    }

    protected async onReferenceImageDropped(files: File[]): Promise<void> {
        if (this.$referenceControlsDisabled()) return
        const file = files[0]
        if (!file) return
        try {
            await this.$operator().setReferenceFromFile(file)
        } catch {
            this.notifications.showErrorString("Could not read the image file.")
        }
    }

    protected async onReferenceRegionChange(region: Box2Like | undefined): Promise<void> {
        if (this.$referenceControlsDisabled()) return
        try {
            await this.$operator().setReferenceRegion(region)
        } catch {
            this.notifications.showErrorString("Could not compute target color statistics.")
        }
    }

    protected async onColorPicked(rgb: [number, number, number]): Promise<void> {
        if (this.$referenceControlsDisabled()) return
        const operator = this.$operator()
        try {
            await operator.runTargetStatsComputation(async () => {
                const {l, a, b} = await computeLabMeanFromColor(rgb, operator.callback.imageOpSystem)
                // Only the mean shifts to the picked color; stddev is left at the source image's own
                // (or, failing that, whatever is currently set) so the texture's detail/contrast is
                // preserved instead of being flattened to a solid color.
                const sourceStats = operator.getSourceStatsForCurrentTextureType()
                operator.clearReference()
                operator.setTargetStats({
                    l: {mean: l, stddev: sourceStats?.l.stddev ?? operator.$targetStddevL()},
                    a: {mean: a, stddev: sourceStats?.a.stddev ?? operator.$targetStddevA()},
                    b: {mean: b, stddev: sourceStats?.b.stddev ?? operator.$targetStddevB()},
                })
            })
        } catch {
            this.notifications.showErrorString("Could not compute target color statistics.")
        }
    }

    protected readonly $targetStatsRows = computed(() => {
        const operator = this.$operator()
        const ifSet = (set: (value: number) => void) => (value: number | null | undefined) => {
            if (value != null) set(value)
        }
        return [
            {
                label: "L",
                mean: operator.$targetMeanL(),
                stddev: operator.$targetStddevL(),
                setMean: ifSet((value) => operator.setTargetMeanL(value)),
                setStddev: ifSet((value) => operator.setTargetStddevL(value)),
            },
            {
                label: "a",
                mean: operator.$targetMeanA(),
                stddev: operator.$targetStddevA(),
                setMean: ifSet((value) => operator.setTargetMeanA(value)),
                setStddev: ifSet((value) => operator.setTargetStddevA(value)),
            },
            {
                label: "b",
                mean: operator.$targetMeanB(),
                stddev: operator.$targetStddevB(),
                setMean: ifSet((value) => operator.setTargetMeanB(value)),
                setStddev: ifSet((value) => operator.setTargetStddevB(value)),
            },
        ]
    })

    readonly $luminance = computed(() => this.$operator().$luminance())

    protected setLuminance(value: number) {
        this.$operator().setLuminance(value)
    }

    readonly $colorIntensity = computed(() => this.$operator().$colorIntensity())

    protected setColorIntensity(value: number) {
        this.$operator().setColorIntensity(value)
    }

    readonly $fade = computed(() => this.$operator().$fade())

    protected setFade(value: number) {
        this.$operator().setFade(value)
    }
}
